import {
    createAsyncThunk,
    createSlice,
    PayloadAction
} from '@reduxjs/toolkit';
import HttpManager from '../../services/HttpManager';

interface Tuition {
    id: number;
    discount: number;
    typeOfDiscount: number;
}

interface Article {
    id: number;
    discount: number;
    typeOfDiscount: number;
}

interface InvoiceItems {
    tuitions: Set<Tuition>;
    articles: Set<Article>;
}

const invoiceItems: InvoiceItems = {
    tuitions: new Set<Tuition>(),
    articles: new Set<Article>()
}

const initialState: any = {
    refresh: false,
    showModal: false,
    modalContent: 0,
    studentData: {},
    selectedInvoice: null,
    selectedCredit: null,
    refundItems: new Set<number>(),
    totalToPaid: 0,
    isPartialPayment: false,
    isCancelPayment: false,
    loadingInvoice: false,
    loadingInvoicePdf: false,
    selectedPayment: {},
    error: null,
    invoiceItems
}

const http = HttpManager.getInstance();
export const handleSelectedInvoice = createAsyncThunk(
    "invoices/handleSelectedInvoice",
    async ({ sessionToken, campusId, studentId, invoiceId }: any, { rejectWithValue }) => {
        try {
            const response = await http.invoiceService.getInvoiceById(
                sessionToken,
                campusId,
                studentId,
                invoiceId
            );
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

/*
	Saca el nombre del archivo de la cabecera Content-Disposition. Acepta las dos
	formas que manda Laravel: filename="..." y filename*=UTF-8''...
*/
export const nombreDelArchivo = (response: any, porOmision: string): string => {
	const cabecera = String(
		response?.headers?.["content-disposition"] ?? response?.headers?.["Content-Disposition"] ?? ""
	);

	const codificado = cabecera.match(/filename\*=UTF-8''([^;]+)/i);

	if (codificado) {
		try {
			return decodeURIComponent(codificado[1].trim().replace(/^"|"$/g, ""));
		} catch (e) {
			// cabecera mal formada: se usa el nombre de siempre
		}
	}

	const simple = cabecera.match(/filename="?([^";]+)"?/i);

	return simple ? simple[1].trim() : porOmision;
};

export const handleInvoicePdf = createAsyncThunk(
    "invoices/handleInvoicePdf",
    async ({ campusId, studentId, invoiceId }: any, { rejectWithValue }) => {
        try {
            const response = await http.fileService.getInvoicePDF(campusId, studentId, invoiceId);
            /*
                El tipo va A PROPOSITO en el Blob: sin el, el navegador no sabe que
                es un PDF y lo baja en vez de enseñarlo. (Esteban, 2026-09-20)
            */
            const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
            /*
                El servidor manda el nombre en Content-Disposition
                ("SPS - Cesia Yaquelin Fajardo Cortez - S97LQOZJ4M - 08-10-2024.pdf"),
                pero aqui se pisaba con "invoice_1593.pdf" y nunca se veia.
                (Esteban, 2026-09-16)
            */
            return { invoiceId, url, nombre: nombreDelArchivo(response, `invoice_${invoiceId}.pdf`) };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

const invoices = createSlice({
    name: 'invoices',
    initialState,
    reducers: {
        setRefresh: (state, action) => {
            state.refresh = action.payload;
        },
        setShowModal: (state, action) => {
            state.showModal = action.payload;
        },
        setModalContent: (state, action) => {
            state.modalContent = action.payload;
        },
        setStudentData: (state, action) => {
            state.studentData = action.payload;
        },
        setSelectedInvoice: (state, action) => {
            state.selectedInvoice = action.payload;
        },
        setSelectedPayment: (state, action) => {
            state.selectedPayment = action.payload;
        },
        setIsNewInvoice: (state, action) => {
            state.isNewInvoice = action.payload;
        },
        setSelectedCredit: (state, action) => {
            state.selectedCredit = action.payload;
        },
        setTotalToPaid: (state, action) => {
            state.totalToPaid = action.payload;
        },
        setIsPartialPayment: (state, action) => {
            state.isPartialPayment = action.payload;
        },
        setIsCancelPayment: (state, action) => {
            state.isCancelPayment = action.payload;
        },
        setRefundItems: (state, action: PayloadAction<any>) => {
            state.refundItems = action.payload;
        },
        handleRefundItemsSelection: (state, action: PayloadAction<any>) => {
            const item = action.payload;
            if (state.refundItems.has(item.id)) {
                state.refundItems.delete(item.id);
            } else {
                state.refundItems.add(item.id);
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(handleSelectedInvoice.pending, (state: any) => {
                state.loadingInvoice = true;
            })
            .addCase(handleSelectedInvoice.fulfilled, (state: any) => {
                state.loadingInvoice = false;
            })
            .addCase(handleSelectedInvoice.rejected, (state: any, action: any) => {
                state.loadingInvoice = false;
                state.error = action.payload;
            })
            .addCase(handleInvoicePdf.pending, (state: any) => {
                state.loadingInvoicePdf = true;
            })
            .addCase(handleInvoicePdf.fulfilled, (state: any) => {
                state.loadingInvoicePdf = false;
            })
            .addCase(handleInvoicePdf.rejected, (state: any, action: any) => {
                state.loadingInvoicePdf = false;
                state.error = action.payload;
            });
    }
});


export const {
    setShowModal,
    setRefresh,
    setModalContent,
    setStudentData,
    setSelectedInvoice,
    setSelectedCredit,
    setRefundItems,
    handleRefundItemsSelection,
    setSelectedPayment,
    setTotalToPaid,
    setIsPartialPayment,
    setIsCancelPayment,
    setIsNewInvoice
} = invoices.actions;

export default invoices.reducer;
